import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import { fetchReferrals } from "../apiService";
import LoginModal from "../Components/LoginModal"; // adjust path to match your folder structure
import "../App.css";

// Statuses confirmed so far from Swagger sample: "pending".
// TODO: confirm the full enum (e.g. "activated" / "redeemed" / "expired" /
// "declined") with Max — the badge logic below treats anything matching
// activated/redeemed/completed as "done" (green) and everything else as
// "pending" (amber), which should cover the common cases either way.
const isActivatedStatus = (status?: string) => {
  if (!status) return false;
  const s = status.toLowerCase();
  return s === "activated" || s === "redeemed" || s === "completed";
};

interface ReferralRow {
  id: number;
  referralCode?: string;
  referredName?: string;
  referredMobileNumber?: string;
  status?: string;
  redeemedAt?: string;
  consentAccepted?: boolean;
}

const ReferralHistory = () => {
  const [referrals, setReferrals] = useState<ReferralRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Whether to show the login modal as an overlay on top of this page.
  // Purely local state — never redirects or changes the URL. The page
  // itself stays mounted and visible behind the modal the whole time.
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    let accessToken: string | null = null;
    let portalEndCustomerId: string | null = null;

    try {
      accessToken = localStorage.getItem("accessToken");
      portalEndCustomerId = localStorage.getItem("portalEndCustomerId");
    } catch (err) {
      console.error(
        "ReferralHistory: localStorage access blocked (private mode / ITP):",
        err
      );
    }

    if (!accessToken || !portalEndCustomerId) {
      setLoading(false);
      setShowLoginModal(true);
      return;
    }

    setShowLoginModal(false);

    const load = async () => {
      try {
        setLoading(true);
        setErrorMsg(null);
        const result = await fetchReferrals();
        if (!result.success) {
          throw new Error(result.message || "Failed to load referrals.");
        }
        const rows = result.data || [];
        setReferrals(rows);

        // Dev-time guard: if the endpoint starts returning referrals with no
        // recognizable id/status (e.g. backend shape changes), this makes
        // that obvious in the console instead of just showing an empty list.
        if (rows.length > 0 && rows.some((r) => r.id === undefined)) {
          console.warn(
            "ReferralHistory: received rows without an id — check that " +
              "fetchReferrals() in apiService.ts still matches the " +
              "/referrals/referrals response shape."
          );
        }
      } catch (error) {
        setErrorMsg(
          error instanceof Error ? error.message : "Failed to load referrals."
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const totalCount = referrals.length;
  const activatedCount = referrals.filter((r) =>
    isActivatedStatus(r.status)
  ).length;
  const pendingCount = totalCount - activatedCount;
  const earnedTotal = activatedCount * 100;
  const pendingTotal = pendingCount * 100;

  const formatDate = (value?: string) => {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-ZA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const maskPhone = (phone?: string) => {
    if (!phone || phone.length < 4) return phone || "—";
    return `${phone.slice(0, 3)} ${"*".repeat(
      Math.max(phone.length - 6, 0)
    )} ${phone.slice(-3)}`;
  };

  return (
    <div className="referral-page">
      {/* LOGIN OVERLAY — shown on top of the page when the visitor isn't
          signed in. The page stays rendered behind it; this never
          redirects or changes the URL. */}
      {showLoginModal && (
        <LoginModal handleClose={() => setShowLoginModal(false)} />
      )}

      <section className="referral-form-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9}>
              <div className="rh-header-row">
                <h2 className="form-title">Your Referrals</h2>
                <Link to="/referral" className="rp-back-link">
                  <i className="bi bi-plus-circle"></i> Refer another friend
                </Link>
              </div>
              <p className="rp-subtitle">
                Track every friend you've referred and follow up on the ones
                still pending — every referral is a shot at R100.
              </p>

              {/* SUMMARY STATS */}
              <Row className="g-3 rh-stats-row">
                <Col sm={4}>
                  <Card className="rh-stat-card">
                    <Card.Body>
                      <span className="rh-stat-value">{totalCount}</span>
                      <span className="rh-stat-label">Total Referrals</span>
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={4}>
                  <Card className="rh-stat-card rh-stat-green">
                    <Card.Body>
                      <span className="rh-stat-value">R{earnedTotal}</span>
                      <span className="rh-stat-label">
                        Earned ({activatedCount} activated)
                      </span>
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={4}>
                  <Card className="rh-stat-card rh-stat-amber">
                    <Card.Body>
                      <span className="rh-stat-value">R{pendingTotal}</span>
                      <span className="rh-stat-label">
                        Pending ({pendingCount} awaiting activation)
                      </span>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card className="rf-card rh-list-card">
                <Card.Body>
                  {errorMsg && (
                    <div className="alert alert-danger">{errorMsg}</div>
                  )}

                  {loading ? (
                    <div className="rh-empty">Loading your referrals...</div>
                  ) : referrals.length === 0 ? (
                    <div className="rh-empty">
                      <i className="bi bi-people"></i>
                      <p>
                        You haven't referred anyone yet.{" "}
                        <Link to="/referral">Refer a friend</Link> to start
                        earning.
                      </p>
                    </div>
                  ) : (
                    <div className="rh-list">
                      {referrals.map((r) => {
                        const activated = isActivatedStatus(r.status);
                        return (
                          <div key={r.id} className="rh-row">
                            <div
                              className={`rh-status-dot ${
                                activated ? "rh-dot-green" : "rh-dot-amber"
                              }`}
                            />
                            <div className="rh-row-main">
                              <span className="rh-row-name">
                                {r.referredName || "Unnamed friend"}
                              </span>
                              <span className="rh-row-sub">
                                {maskPhone(r.referredMobileNumber)}
                                {r.referralCode && (
                                  <> · Ref: {r.referralCode}</>
                                )}
                              </span>
                            </div>
                            <div className="rh-row-status">
                              <span
                                className={`rh-badge ${
                                  activated
                                    ? "rh-badge-green"
                                    : "rh-badge-amber"
                                }`}
                              >
                                {activated ? "Activated" : "Pending"}
                              </span>
                              <span className="rh-row-date">
                                {activated
                                  ? formatDate(r.redeemedAt)
                                  : "Follow up with your friend"}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <style>{`
        .form-title { font-weight: 700; margin: 0; }
        .rh-header-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-bottom: 0.5rem;
        }
        .rp-back-link {
          font-size: 0.85rem;
          font-weight: 600;
          color: #e65c1c;
          text-decoration: none;
        }
        .rp-subtitle {
          color: #6b6b6b;
          font-size: 0.9rem;
          margin-bottom: 1.5rem;
        }
        .rh-stats-row { margin-bottom: 1.5rem; }
        .rh-stat-card {
          border: none;
          border-radius: 16px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.06);
          height: 100%;
        }
        .rh-stat-card .card-body {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          padding: 1.1rem 1.2rem;
        }
        .rh-stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f1f1f;
        }
        .rh-stat-label {
          font-size: 0.78rem;
          color: #7a7a7a;
        }
        .rh-stat-green .rh-stat-value { color: #2f9e44; }
        .rh-stat-amber .rh-stat-value { color: #e65c1c; }
        .rf-card {
          border-radius: 20px;
          border: none;
          box-shadow: 0 8px 30px rgba(0,0,0,0.08);
        }
        .rh-list-card .card-body { padding: 1.5rem; }
        .rh-empty {
          text-align: center;
          padding: 2.5rem 1rem;
          color: #7a7a7a;
        }
        .rh-empty i {
          font-size: 2rem;
          color: #e2e2e2;
          display: block;
          margin-bottom: 0.75rem;
        }
        .rh-empty a { color: #e65c1c; font-weight: 600; }
        .rh-list {
          display: flex;
          flex-direction: column;
        }
        .rh-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem 0.25rem;
          border-bottom: 1px solid #f0f0f0;
        }
        .rh-row:last-child { border-bottom: none; }
        .rh-status-dot {
          flex-shrink: 0;
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }
        .rh-dot-green { background: #2f9e44; }
        .rh-dot-amber { background: #e65c1c; }
        .rh-row-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-width: 0;
        }
        .rh-row-name {
          font-weight: 700;
          font-size: 0.95rem;
          color: #1f1f1f;
        }
        .rh-row-sub {
          font-size: 0.78rem;
          color: #9a9a9a;
        }
        .rh-row-status {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0.25rem;
          flex-shrink: 0;
        }
        .rh-badge {
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.03em;
          padding: 0.25rem 0.65rem;
          border-radius: 999px;
        }
        .rh-badge-green { background: #eef8ee; color: #2f9e44; }
        .rh-badge-amber { background: #fff6f1; color: #e65c1c; }
        .rh-row-date {
          font-size: 0.72rem;
          color: #9a9a9a;
        }
        @media (max-width: 480px) {
          .rh-row-status { align-items: flex-start; }
        }
      `}</style>
    </div>
  );
};

export default ReferralHistory;